import {
  writeActiveChatIds,
  type ActiveChatIdsByWorkspace,
} from "./activeChatStorage";
import { writeDraftText } from "./draftTextStorage";
import {
  applyChatEvent,
  failAssistantMessage,
  resetAssistantMessage,
  upsertAssistantMessage,
  type UpdateAssistantMessage,
} from "./messageReducer";
import { runChatStream, type StreamRunResult } from "./streamRunner";
import type {
  ChatActions,
  ChatEntry,
  ChatState,
  ChatStatus,
  ChatStreamEvent,
  ChatTransport,
  FetchChatResult,
  OpenChatMode,
  PageContext,
  PageContextGetter,
  StreamMode,
  UnifiedAgentViewState,
  UnifiedChatMessage,
  UnifiedChatUserMessage,
  UnifiedLoadedChat,
} from "./types";
import {
  getAgentQuestionAnswerItems,
  getPendingAgentQuestions,
} from "@/components/analysis/chat/v2/agentQuestions";
import {
  getUserFacingStreamError,
  hasSuccessfulAssistantAfterUser,
} from "@/components/analysis/chat/v2/hooks/useChatStreaming";
import type { FileRef } from "@/components/analysis/chat/v2/utils/streamChatMessage";
import { trackError } from "@/components/error/trackError";
import { formatPromptAnswers } from "@/components/ui/chat-composer/promptAnswers";
import { fireAndForget } from "@/lib/vercelUtils";
import { assertNever } from "@/packages/ts-utils";
import { zodBrandedUuid } from "@/utils/helpers";
import { logger } from "@/utils/logging/browser";
import { errorPlaceTag } from "@/utils/o11y/tags";
import { trackSystemMetric } from "@/utils/systemMetrics/useSystemMetrics";
import type {
  AnalysisChatV2Id,
  AnalysisChatV2MessageId,
  OrganizationId,
  SurveyId,
} from "@/utils/types";
import { v4 as uuidv4 } from "uuid";
import { createStore, type StoreApi } from "zustand";

const NEW_CHAT_TITLE = "New chat";
const MAX_TITLE_LENGTH = 100;

export type UnifiedChatStoreState = {
  chats: Partial<Record<AnalysisChatV2Id, ChatEntry>>;
  chatOrgIds: Partial<Record<AnalysisChatV2Id, OrganizationId>>;
  activeChatIds: ActiveChatIdsByWorkspace;
  viewState: UnifiedAgentViewState;
};

export type UnifiedChat = {
  store: StoreApi<UnifiedChatStoreState>;
  openChat: (args: {
    chatId: AnalysisChatV2Id;
    orgId: OrganizationId;
    mode: OpenChatMode;
  }) => void;
  selectChat: (args: {
    orgId: OrganizationId;
    chatId: AnalysisChatV2Id;
  }) => void;
  /** Makes an already open chat the org's active one, without fetching it again. */
  activateChat: (args: {
    orgId: OrganizationId;
    chatId: AnalysisChatV2Id;
  }) => void;
  startNewChat: (args: { orgId: OrganizationId }) => AnalysisChatV2Id;
  /** Opens the org's stored active chat, or starts a new one. */
  ensureActiveChat: (args: { orgId: OrganizationId }) => void;
  setViewState: (viewState: UnifiedAgentViewState) => void;
  subscribeToEvents: (listener: (event: ChatStreamEvent) => void) => () => void;
  /** Every sent or retried turn awaits the registered getter once before it starts. Returns an unregister function that only removes this getter. */
  registerPageContext: (getter: PageContextGetter) => () => void;
  /** Aborts the chat's running turn and drops it from the store, e.g. after the server deleted it. */
  removeChat: (chatId: AnalysisChatV2Id) => void;
  /** Aborts every running turn and drops all listeners. */
  dispose: () => void;
};

type TurnRequest = {
  chatId: AnalysisChatV2Id;
  mode: StreamMode;
  userMessage: UnifiedChatUserMessage;
  assistantMessageId: AnalysisChatV2MessageId;
};

type Turn = TurnRequest & {
  orgId: OrganizationId;
  surveyIds: SurveyId[] | undefined;
  pageContext: PageContext | undefined;
  isNewChat: boolean;
  abortController: AbortController;
};

type TurnOutcome = { type: "done" } | { type: "failed"; error: unknown };

type Load = { orgId: OrganizationId };

export const mintChatId = () =>
  zodBrandedUuid<AnalysisChatV2Id>().parse(uuidv4());

const mintMessageId = () =>
  zodBrandedUuid<AnalysisChatV2MessageId>().parse(uuidv4());

const createUserMessage = (
  text: string,
  files: FileRef[] | undefined
): UnifiedChatUserMessage => ({
  id: mintMessageId(),
  role: "user",
  text,
  timestamp: new Date(),
  ...(files?.length ? { files } : {}),
});

const titleFromMessage = (text: string) =>
  text.length > MAX_TITLE_LENGTH
    ? `${text.slice(0, MAX_TITLE_LENGTH)}...`
    : text;

export const isChatBusy = (status: ChatStatus): boolean => {
  switch (status.type) {
    case "preparing":
    case "ai-working":
      return true;
    case "idle":
    case "error":
      return false;
    default:
      return assertNever(status);
  }
};

const isUserMessage = (
  message: UnifiedChatMessage
): message is UnifiedChatUserMessage => message.role === "user";

// Intermediate iterations are persisted mid-turn, so the server's pending id beats "last message is from the user".
const findPendingUserMessage = (
  chat: UnifiedLoadedChat
): UnifiedChatUserMessage | undefined => {
  if (chat.pendingMessageId) {
    return chat.messages
      .filter(isUserMessage)
      .find((message) => message.id === chat.pendingMessageId);
  }
  const lastMessage = chat.messages[chat.messages.length - 1];
  return lastMessage && isUserMessage(lastMessage) ? lastMessage : undefined;
};

/** All chats of the app: state lives in a zustand store, in-flight turns and event listeners next to it. */
export const createUnifiedChat = ({
  transport,
  initialActiveChatIds,
}: {
  transport: ChatTransport;
  initialActiveChatIds: ActiveChatIdsByWorkspace;
}): UnifiedChat => {
  const store = createStore<UnifiedChatStoreState>(() => ({
    chats: {},
    chatOrgIds: {},
    activeChatIds: initialActiveChatIds,
    viewState: "closed",
  }));
  // At most one turn per chat. A turn that is no longer in this map was aborted or replaced and must not touch state.
  const turns = new Map<AnalysisChatV2Id, Turn>();
  // At most one load per chat, by identity like `turns`: a replaced or removed load must not write its result.
  const loads = new Map<AnalysisChatV2Id, Load>();
  const eventListeners = new Set<(event: ChatStreamEvent) => void>();
  // A page has exactly one context, so only one getter can be registered at a time.
  let pageContextGetter: PageContextGetter | null = null;

  const setEntry = (chatId: AnalysisChatV2Id, entry: ChatEntry) =>
    store.setState((state) => ({ chats: { ...state.chats, [chatId]: entry } }));

  const getChat = (chatId: AnalysisChatV2Id): ChatState | undefined => {
    const entry = store.getState().chats[chatId];
    return entry?.type === "loaded" ? entry.state : undefined;
  };

  // Only loaded chats can change; updates to any other entry are dropped.
  const updateChat = (
    chatId: AnalysisChatV2Id,
    update: (chat: ChatState) => Partial<ChatState>
  ) => {
    const chat = getChat(chatId);
    if (chat) {
      setEntry(chatId, {
        type: "loaded",
        state: { ...chat, ...update(chat) },
      });
    }
  };

  const createChatState = ({
    chatId,
    orgId: workspaceId,
  }: {
    chatId: AnalysisChatV2Id;
    orgId: OrganizationId;
  }): ChatState => ({
    id: chatId,
    orgId: workspaceId,
    title: NEW_CHAT_TITLE,
    messages: [],
    isPersisted: false,
    isReadOnly: false,
    slackChannelName: undefined,
    hasTurnInThisTab: false,
    status: { type: "idle" },
    draft: { surveyIds: undefined },
    actions: createChatActions(chatId),
  });

  const updateAssistantMessage = (
    turn: Pick<Turn, "chatId" | "assistantMessageId">,
    update: UpdateAssistantMessage
  ) =>
    updateChat(turn.chatId, (chat) => ({
      messages: upsertAssistantMessage({
        messages: chat.messages,
        assistantMessageId: turn.assistantMessageId,
        update,
      }),
    }));

  const isCurrent = (turn: Turn) => turns.get(turn.chatId) === turn;

  const emit = (event: ChatStreamEvent) =>
    eventListeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        logger.captureException(
          error,
          errorPlaceTag.withValue("unified_chat.stream_event_listener")
        );
      }
    });

  const applyLoadedChat = ({
    chat,
    orgId: workspaceId,
  }: {
    chat: UnifiedLoadedChat;
    orgId: OrganizationId;
  }) => {
    const current =
      getChat(chat.id) ??
      createChatState({ chatId: chat.id, orgId: workspaceId });
    setEntry(chat.id, {
      type: "loaded",
      state: {
        ...current,
        title: chat.title,
        messages: chat.messages,
        isPersisted: true,
        isReadOnly: chat.isReadOnly ?? false,
        slackChannelName: chat.slackChannelName,
        draft: {
          ...current.draft,
          surveyIds: current.draft.surveyIds ?? chat.surveyIds,
        },
      },
    });
  };

  const fetchChat = async (args: {
    chatId: AnalysisChatV2Id;
    orgId: OrganizationId;
  }): Promise<FetchChatResult> => {
    try {
      return await transport.fetchChat(args);
    } catch (error) {
      return {
        type: "failed",
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  };

  // The stream can die after the server saved the answer; this checks the database before calling the turn failed.
  const recoverSavedAnswer = async (turn: Turn): Promise<boolean> => {
    const result = await fetchChat({ chatId: turn.chatId, orgId: turn.orgId });
    const didServerAnswer =
      result.type === "found" &&
      hasSuccessfulAssistantAfterUser({
        chat: result.chat,
        userMessageId: turn.userMessage.id,
      });
    if (result.type === "found" && isCurrent(turn)) {
      if (didServerAnswer) {
        applyLoadedChat({ chat: result.chat, orgId: turn.orgId });
      } else {
        // The server can create the row and fail before `init`.
        updateChat(turn.chatId, () => ({ isPersisted: true }));
      }
    }
    return didServerAnswer;
  };

  const failTurn = (turn: Turn, error: unknown) => {
    const message = getUserFacingStreamError({ error });
    trackSystemMetric({ name: "mission_control.chat.error", value: 1 });
    logger.captureException(
      error,
      { mode: turn.mode },
      errorPlaceTag.withValue("unified_chat.fail_turn")
    );
    updateChat(turn.chatId, () => ({ status: { type: "error", message } }));
    updateAssistantMessage(turn, (assistant) =>
      failAssistantMessage(assistant, message)
    );
  };

  const streamTurn = (turn: Turn): Promise<StreamRunResult> => {
    let langsmithTraceId: string | undefined;
    return runChatStream({
      isNewChat: turn.isNewChat,
      replayOnly: turn.mode === "resume",
      signal: turn.abortController.signal,
      onRetry: ({ reason }) => {
        if (reason === "stall") {
          trackSystemMetric({ name: "mission_control.chat.stall", value: 1 });
        }
        trackSystemMetric({
          name: "mission_control.chat.retry",
          value: 1,
          tags: { exhausted: "false" },
        });
      },
      onRetriesExhausted: () =>
        trackSystemMetric({
          name: "mission_control.chat.retry",
          value: 1,
          tags: { exhausted: "true" },
        }),
      openStream: (attempt) =>
        transport.sendMessage({
          ...attempt,
          orgId: turn.orgId,
          chatId: turn.chatId,
          messageId: turn.userMessage.id,
          message: turn.userMessage.text,
          files: turn.userMessage.files,
          agentQuestionAnswers: turn.userMessage.agentQuestionAnswers,
          surveyIds: attempt.replayOnly ? undefined : turn.surveyIds,
          pageContext: attempt.replayOnly ? undefined : turn.pageContext,
        }),
      onEvent: ({ event, eventIndex }) => {
        if (!isCurrent(turn)) {
          return;
        }
        if (event.type === "init") {
          langsmithTraceId = event.langsmithTraceId;
          // The server creates the chat before it sends `init`.
          updateChat(turn.chatId, () => ({ isPersisted: true }));
        }
        updateAssistantMessage(turn, (assistant) => ({
          ...applyChatEvent(assistant, event),
          ...(event.type === "finish" && langsmithTraceId
            ? { langsmithTraceId }
            : {}),
        }));
        emit({
          chatId: turn.chatId,
          userMessageId: turn.userMessage.id,
          assistantMessageId: turn.assistantMessageId,
          mode: turn.mode,
          eventIndex,
          event,
        });
      },
    });
  };

  const playTurn = async (turn: Turn): Promise<TurnOutcome> => {
    // A retried message may already have an answer; sending it again could run the agent twice.
    if (turn.mode === "retry") {
      const didAlreadyAnswer = await recoverSavedAnswer(turn);
      if (didAlreadyAnswer) {
        return { type: "done" };
      }
    }
    const result = await streamTurn(turn);
    switch (result.type) {
      case "completed":
      case "aborted":
        return { type: "done" };
      case "failed":
        return (await recoverSavedAnswer(turn)) ? { type: "done" } : result;
      default:
        return assertNever(result);
    }
  };

  // A rejecting getter is a bug; the turn still runs, without page context, instead of staying stuck.
  const getPageContext = async (): Promise<PageContext | undefined> => {
    try {
      return await pageContextGetter?.();
    } catch (error) {
      fireAndForget(
        trackError({
          error: error instanceof Error ? error : new Error(String(error)),
          componentStack: null,
          eventId: null,
          errorPlace: "unifiedChat.getPageContext",
        })
      );
      return undefined;
    }
  };

  // A resume replays a running turn without page context, so it must not run the getter's side effects.
  const getTurnPageContext = async (
    turn: Turn
  ): Promise<PageContext | undefined> => {
    switch (turn.mode) {
      case "send":
      case "retry":
        return getPageContext();
      case "resume":
        return undefined;
      default:
        return assertNever(turn.mode);
    }
  };

  // Pages lock and save while a turn is preparing, which a resume must not trigger.
  const getStartingStatus = (turn: Turn): ChatStatus => {
    switch (turn.mode) {
      case "send":
      case "retry":
        return { type: "preparing" };
      case "resume":
        return { type: "ai-working", pageContext: undefined };
      default:
        return assertNever(turn.mode);
    }
  };

  const runTurn = async (request: TurnRequest) => {
    const chat = getChat(request.chatId);
    if (!chat) {
      return;
    }
    const preparingTurn: Turn = {
      ...request,
      orgId: chat.orgId,
      // An empty selection means the picker is open with nothing picked, which scopes to all studies.
      surveyIds: chat.draft.surveyIds?.length
        ? chat.draft.surveyIds
        : undefined,
      pageContext: undefined,
      isNewChat: !chat.isPersisted,
      abortController: new AbortController(),
    };
    turns.set(preparingTurn.chatId, preparingTurn);
    updateChat(preparingTurn.chatId, () => ({
      status: getStartingStatus(preparingTurn),
      hasTurnInThisTab: true,
    }));
    const pageContext = await getTurnPageContext(preparingTurn);
    if (!isCurrent(preparingTurn)) {
      return;
    }
    const turn: Turn = { ...preparingTurn, pageContext };
    turns.set(turn.chatId, turn);
    updateChat(turn.chatId, () => ({
      status: { type: "ai-working", pageContext },
    }));
    try {
      const outcome = await playTurn(turn).catch(
        (error: unknown): TurnOutcome => ({ type: "failed", error })
      );
      if (!isCurrent(turn)) {
        return;
      }
      switch (outcome.type) {
        case "done":
          updateChat(turn.chatId, () => ({ status: { type: "idle" } }));
          return;
        case "failed":
          failTurn(turn, outcome.error);
          return;
        default:
          assertNever(outcome);
      }
    } finally {
      if (isCurrent(turn)) {
        turns.delete(turn.chatId);
      }
    }
  };

  const canStartTurn = (chat: ChatState) =>
    !chat.isReadOnly && !turns.has(chat.id);

  const startUserTurn = ({
    chatId,
    userMessage,
  }: {
    chatId: AnalysisChatV2Id;
    userMessage: UnifiedChatUserMessage;
  }) => {
    updateChat(chatId, (current) => ({
      title: current.isPersisted
        ? current.title
        : titleFromMessage(userMessage.text),
      messages: [...current.messages, userMessage],
    }));
    fireAndForget(
      runTurn({
        chatId,
        mode: "send",
        userMessage,
        assistantMessageId: mintMessageId(),
      })
    );
  };

  const createChatActions = (chatId: AnalysisChatV2Id): ChatActions => ({
    setDraftText: (text) => writeDraftText(chatId, text),
    setDraftSurveyIds: (surveyIds) =>
      updateChat(chatId, (chat) => ({ draft: { ...chat.draft, surveyIds } })),
    send: (rawText, files) => {
      const chat = getChat(chatId);
      const text = rawText.trim();
      if (!chat || !canStartTurn(chat) || text.length === 0) {
        return false;
      }
      writeDraftText(chatId, "");
      startUserTurn({ chatId, userMessage: createUserMessage(text, files) });
      return true;
    },
    answerAgentQuestions: (answers) => {
      const chat = getChat(chatId);
      if (!chat || !canStartTurn(chat)) {
        return false;
      }
      const items = getAgentQuestionAnswerItems({
        questions: getPendingAgentQuestions(chat.messages) ?? [],
        answers,
      });
      if (!items) {
        return false;
      }
      writeDraftText(chatId, "");
      startUserTurn({
        chatId,
        userMessage: {
          ...createUserMessage(formatPromptAnswers(items), undefined),
          agentQuestionAnswers: answers,
        },
      });
      return true;
    },
    retry: ({ assistantMessageId, userMessage }) => {
      const chat = getChat(chatId);
      if (!chat || !canStartTurn(chat)) {
        return;
      }
      updateAssistantMessage(
        { chatId, assistantMessageId },
        resetAssistantMessage
      );
      fireAndForget(
        runTurn({ chatId, mode: "retry", userMessage, assistantMessageId })
      );
    },
    abort: () => {
      turns.get(chatId)?.abortController.abort();
      turns.delete(chatId);
      updateChat(chatId, () => ({ status: { type: "idle" } }));
    },
  });

  const fetchChatIntoStore = async ({
    chatId,
    orgId: workspaceId,
    onNotFound,
  }: {
    chatId: AnalysisChatV2Id;
    orgId: OrganizationId;
    onNotFound?: () => void;
  }) => {
    const load: Load = { orgId: workspaceId };
    loads.set(chatId, load);
    setEntry(chatId, { type: "loading" });
    const result = await fetchChat({ chatId, orgId: workspaceId });
    if (loads.get(chatId) !== load) {
      return;
    }
    loads.delete(chatId);
    switch (result.type) {
      case "found": {
        applyLoadedChat({ chat: result.chat, orgId: workspaceId });
        const pendingUserMessage = findPendingUserMessage(result.chat);
        if (pendingUserMessage && !turns.has(chatId)) {
          await runTurn({
            chatId,
            mode: "resume",
            userMessage: pendingUserMessage,
            assistantMessageId: mintMessageId(),
          });
        }
        return;
      }
      // The id may still exist for another org, a study, or as a deleted row, so it is never reused for a new chat.
      case "not-found":
        setEntry(chatId, { type: "not-found" });
        onNotFound?.();
        return;
      case "failed":
        fireAndForget(
          trackError({
            error: result.error,
            componentStack: null,
            eventId: null,
            errorPlace: "unifiedChat.fetchChatIntoStore",
          })
        );
        setEntry(chatId, { type: "load-failed" });
        return;
      default:
        assertNever(result);
    }
  };

  const openChatWithFallback = ({
    chatId,
    orgId: workspaceId,
    mode,
    onNotFound,
  }: {
    chatId: AnalysisChatV2Id;
    orgId: OrganizationId;
    mode: OpenChatMode;
    onNotFound?: () => void;
  }) => {
    const existing = store.getState().chats[chatId];
    const isLoadedForThisWorkspace =
      existing?.type === "loaded" && existing.state.orgId === workspaceId;
    const isLoadingForThisWorkspace =
      existing?.type === "loading" && loads.get(chatId)?.orgId === workspaceId;
    if (isLoadedForThisWorkspace || isLoadingForThisWorkspace) {
      return;
    }
    store.setState((state) => ({
      chatOrgIds: { ...state.chatOrgIds, [chatId]: workspaceId },
    }));
    turns.get(chatId)?.abortController.abort();
    turns.delete(chatId);
    loads.delete(chatId);
    switch (mode) {
      case "new":
        setEntry(chatId, {
          type: "loaded",
          state: createChatState({ chatId, orgId: workspaceId }),
        });
        return;
      case "existing":
        fireAndForget(
          fetchChatIntoStore({ chatId, orgId: workspaceId, onNotFound })
        );
        return;
      default:
        assertNever(mode);
    }
  };

  const openChat: UnifiedChat["openChat"] = openChatWithFallback;

  const activateChat = ({
    orgId: workspaceId,
    chatId,
  }: {
    orgId: OrganizationId;
    chatId: AnalysisChatV2Id;
  }) => {
    store.setState((state) => ({
      activeChatIds: { ...state.activeChatIds, [workspaceId]: chatId },
    }));
    writeActiveChatIds(store.getState().activeChatIds);
  };

  const startNewChat: UnifiedChat["startNewChat"] = ({
    orgId: workspaceId,
  }) => {
    const chatId = mintChatId();
    openChat({ chatId, orgId: workspaceId, mode: "new" });
    activateChat({ orgId: workspaceId, chatId });
    return chatId;
  };

  return {
    store,
    openChat,
    selectChat: ({ orgId, chatId }) => {
      openChat({ chatId, orgId, mode: "existing" });
      activateChat({ orgId, chatId });
    },
    activateChat,
    startNewChat,
    ensureActiveChat: ({ orgId }) => {
      const chatId = store.getState().activeChatIds[orgId];
      if (!chatId) {
        startNewChat({ orgId });
        return;
      }
      openChatWithFallback({
        chatId,
        orgId,
        mode: "existing",
        onNotFound: () => {
          if (store.getState().activeChatIds[orgId] === chatId) {
            startNewChat({ orgId });
          }
        },
      });
    },
    setViewState: (viewState) => store.setState({ viewState }),
    subscribeToEvents: (listener) => {
      eventListeners.add(listener);
      return () => {
        eventListeners.delete(listener);
      };
    },
    registerPageContext: (getter) => {
      if (pageContextGetter) {
        fireAndForget(
          trackError({
            error: new Error("A second component registered a page context"),
            componentStack: null,
            eventId: null,
            errorPlace: "unifiedChat.registerPageContext",
          })
        );
      }
      pageContextGetter = getter;
      return () => {
        if (pageContextGetter === getter) pageContextGetter = null;
      };
    },
    removeChat: (chatId) => {
      turns.get(chatId)?.abortController.abort();
      turns.delete(chatId);
      loads.delete(chatId);
      store.setState((state) => {
        const { [chatId]: _removed, ...chats } = state.chats;
        return { chats };
      });
    },
    dispose: () => {
      turns.forEach((turn) => turn.abortController.abort());
      turns.clear();
      loads.clear();
      eventListeners.clear();
      pageContextGetter = null;
    },
  };
};
